import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resetPasswordEmail } from '../utils/api';
import './ForgotPassword.css';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await resetPasswordEmail(email);
      setMessage(response.message || 'Password reset email sent successfully!');
      setEmailSent(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <h2>Reset Your Password</h2>
          <p>Enter your email address and we'll send you a link to reset your password.</p>
        </div>

        {!emailSent ? (
          <form onSubmit={handleSubmit} className="forgot-password-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={isLoading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              className="reset-button"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        ) : (
          <div className="success-state">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" width="48" height="48">
                <circle cx="12" cy="12" r="11" className="success-circle" />
                <path className="success-checkmark" d="M7 13l3 3 7-7" />
              </svg>
            </div>
            
            <div className="success-content">
              <h3>Email Sent!</h3>
              <div className="success-steps">
                <div className="step">
                  <div className="step-number">1</div>
                  <div className="step-text">
                    If an account exists with this email, you will receive password reset instructions.
                  </div>
                </div>
                <div className="step">
                  <div className="step-number">2</div>
                  <div className="step-text">
                    Please check your email and click the link to reset your password.
                  </div>
                </div>
              </div>
              
              <div className="help-box">
                <div className="help-header">
                  <svg viewBox="0 0 24 24" width="20" height="20">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z" />
                  </svg>
                  <span>Didn't receive the email?</span>
                </div>
                <div className="help-actions">
                  <button 
                    onClick={() => {
                      setEmailSent(false);
                      setMessage('');
                      setError('');
                    }}
                    className="try-again-button"
                  >
                    Try Again
                  </button>
                  <span className="help-divider">or</span>
                  <button className="check-spam-button">
                    Check Spam Folder
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="forgot-password-footer">
          <Link to="/login" className="back-to-login">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword; 